import { MantineProvider, Modal } from '@mantine/core';
import { GlobalMessages } from '@shared/localization/messages';
import { type ReactNode, useEffect, useState } from 'react';

import IconX from '@/assets/Icons/IconX';
import { useMessages } from '@/hooks/messages';

import { Button } from './atoms/Button';

export type ModalSize = 's' | 'm' | 'l';

export interface ModalStatelessProps {
  title: string;
  className?: string | undefined;
  open: boolean;
  onClose: () => void;
  buttons?: ReactNode;
  withoutButtons?: boolean;
  size?: ModalSize;
  'data-testid'?: string;
}

// Stacked Mantine modals get a strictly increasing z-index in the order they
// OPEN. JSX nesting depth is not a usable proxy: a modal opened from inside
// another modal is usually rendered as its *sibling* (e.g. the assumptions
// modal on the estimation item detail modal), and Mantine portals in mount
// order — an always-mounted sibling lands earlier in <body> than the
// lazily-mounted modal that opens it, so with equal z-index it renders behind
// it and its overlay swallows the clicks (EWM-591 covered the nested case only).
const BASE_Z_INDEX = 10009;
const Z_INDEX_STEP = 10;
// ponytail: module-level open counter, reset implicitly as modals unmount/close.
// Ceiling — closing an *underlying* modal first frees its slot, so the next
// modal to open can land below one that is still open. Not reachable through
// the UI (you cannot click the covered modal); needs a real stack if it ever is.
let openModalCount = 0;

export const ModalStateless: FCC<ModalStatelessProps> = ({
  title,
  className,
  open,
  onClose,
  buttons,
  withoutButtons,
  children,
  size,
  'data-testid': dataTestId,
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const [zIndex, setZIndex] = useState(BASE_Z_INDEX);

  useEffect(() => {
    if (!open) {
      return;
    }
    setZIndex(BASE_Z_INDEX + openModalCount * Z_INDEX_STEP);
    openModalCount += 1;
    return () => {
      openModalCount -= 1;
    };
  }, [open]);

  const modalSizeClass =
    size === 's'
      ? 'w-full max-w-md'
      : size === 'm'
        ? 'w-full max-w-5xl'
        : 'w-full max-w-screen-2xl';

  return (
    <MantineProvider>
      <Modal.Root
        opened={open}
        onClose={onClose}
        size='auto'
        padding={0}
        centered={false}
        yOffset='2rem'
        zIndex={zIndex}
        transitionProps={{ duration: 200 }}
      >
        <Modal.Overlay color='gray' backgroundOpacity={0.6} />
        {/*
          Apply our styling via `classNames.content` (the Paper), NOT `className`.
          Mantine spreads `Modal.Content`'s `className` onto BOTH the content Paper
          and the inner wrapper (`mantine-Modal-inner`). The inner wrapper relies on
          Mantine's `position: fixed` (in `@layer mantine`); our `.panel` utility is
          unlayered (`position: relative`) and would override it, dropping the modal
          into normal flow at the bottom of the page (EWM-591).
        */}
        <Modal.Content
          classNames={{
            // `flex flex-col` + a scrollable body keeps the header and footer
            // pinned while the body scrolls. Mantine caps `.mantine-Modal-content`
            // at `max-height: 100dvh - 2*yOffset`; `overflow-hidden` here clips to
            // the rounded corners, so the body MUST own the vertical scroll or tall
            // content becomes unreachable below the fold (EWM-647).
            content: `panel flex max-h-[calc(100dvh-4rem)] flex-col overflow-hidden rounded-lg border-0 p-0 text-black dark:bg-c16 dark:text-white ${className ? className : ''} ${modalSizeClass}`,
          }}
          data-testid={dataTestId}
        >
          <div className='dark:bg-c16 flex shrink-0 items-center justify-between px-6 py-6'>
            <h5 className='text-2xl'>{title}</h5>
            <button onClick={onClose} type='button' className='text-white-dark hover:text-dark'>
              <IconX />
            </button>
          </div>
          {/*
            EWM-882: the Paper is `overflow-hidden` (rounded-corner clip), so the
            body must also own the horizontal scroll — otherwise wide content
            (e.g. cost/resourcing tables) is clipped with no way to reach it.
          */}
          <div className='dark:bg-c16 min-h-0 flex-1 overflow-x-auto overflow-y-auto px-6 pt-0 pb-6'>
            {children}
          </div>
          <div className='dark:bg-c16 mb-7 flex shrink-0 justify-between space-x-4 px-6 py-4'>
            {!withoutButtons &&
              (buttons ? (
                buttons
              ) : (
                <Button variant='primary' onClick={onClose}>
                  {globalMessages.close()} <IconX />
                </Button>
              ))}
          </div>
        </Modal.Content>
      </Modal.Root>
    </MantineProvider>
  );
};
